import { TruncatedTextWithTooltip } from '@app/components/base/TruncatedTextWithTooltip';
import { ApplyColumnFilter } from '@app/components/table/ApplyColumnFilter';
import { ShowOnStreamAction } from '@app/modules/audit/components/ShowOnStreamAction';
import { getSpotVerifyMatchTargetFromMatch } from '@app/modules/audit/modules/station-audit/components/verify-match/to-verify-match-target';
import { VerifyMatchTarget } from '@app/modules/audit/modules/station-audit/components/verify-match/verify-match-types';
import { VerifyMatchAction } from '@app/modules/audit/modules/station-audit/components/verify-match/VerifyMatchAction';
import { AuditMatch } from '@app/modules/audit/modules/station-audit/types';
import DownloadIcon from '@mui/icons-material/Download';
import { MRT_ColumnDef } from 'material-react-table';
import { useCallback } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { RowActionMui } from '../../../../../../../../components/table/RowActionMui';
import { RowActions } from '../../../../../../../../components/table/RowActions';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { auditDownloadAudio } from '../../../../actions';
import { useAuditMatchColumns } from '../../../../hooks/useAuditMatchColumns';

const orderColumnHeaderMessage = defineMessage({
  id: 'audit.props.order',
  defaultMessage: 'Orden',
});

const versionColumnHeaderMessage = defineMessage({
  id: 'audit.props.version',
  defaultMessage: 'Versión',
});

export interface UseSpotMatchesColumnsOptions {
  /** Opens the verify-match dialog for the given target. */
  openAt: (target: VerifyMatchTarget) => void;
}

/**
 * Datetime / audio / order / version / status / actions columns for the spot matches table.
 */
export function useSpotMatchesColumns({
  openAt,
}: UseSpotMatchesColumnsOptions): MRT_ColumnDef<AuditMatch>[] {
  const { $t } = useIntl();
  const station = useActiveStation();

  const downloadAudio = useCallback(
    (match: AuditMatch) => {
      auditDownloadAudio({
        stationId: station.id,
        customId: match.audio!.customId,
      });
    },
    [station.id],
  );

  return useAuditMatchColumns({
    timeColumnType: 'datetime',
    actionsColumnSize: 150,
    actionsColumn: (row) => (
      <RowActions>
        <VerifyMatchAction
          onClick={() => openAt(getSpotVerifyMatchTargetFromMatch(row.original))}
        />
        <ShowOnStreamAction
          station={station.slug}
          datetime={row.original.datetime}
          durationMs={row.original.playedDuration}
          tab="audiomatches"
        />
        <RowActionMui
          onClick={() => downloadAudio(row.original)}
          title={
            <FormattedMessage
              id="audit.matchesScreen.downloadBtn"
              defaultMessage="Descargar media"
            />
          }
        >
          <DownloadIcon />
        </RowActionMui>
      </RowActions>
    ),
    afterCustomIdColumns: [
      {
        id: 'matchedSchedule.order',
        header: $t(orderColumnHeaderMessage),
        size: 100,
        enableSorting: true,
        accessorFn: (match) => match.matchedSchedule?.order ?? '',
        Cell: ({ row }) => (
          <ApplyColumnFilter field="order" value={row.original.matchedSchedule?.order}>
            <TruncatedTextWithTooltip value={row.original.matchedSchedule?.order} />
          </ApplyColumnFilter>
        ),
      },
      {
        id: 'matchedSchedule.version',
        header: $t(versionColumnHeaderMessage),
        size: 170,
        accessorFn: (match) => match.matchedSchedule?.version ?? '',
        Cell: ({ row }) => (
          <ApplyColumnFilter field="version" value={row.original.matchedSchedule?.version}>
            <TruncatedTextWithTooltip value={row.original.matchedSchedule?.version} />
          </ApplyColumnFilter>
        ),
      },
    ],
  });
}
